<template>
    <div class="activities-listing-table__container">
        <TableLoading v-if="isTableLoading" />
        <CompassEmptyState
            v-if="shouldDisplayEmptyState"
            v-bind="emptyStateDatas"
            class="activities-listing__empty-state"
        >
            <CompassButton
                v-if="!isEqual(defaultFilters, filters)"
                :label="localizedTexts.labelResetFiltersBtn"
                @click="resetFilters"
            />
        </CompassEmptyState>
        <CompassTable
            v-show="shouldDisplayTable"
            ref="activitiesListingTable"
            :size="sizeTable"
            :border="isActivityPage"
            class="activities-listing-table"
            :class="tableClasses"
            :isCheckable="shouldDisplayMassActionsCheckbox"
            :checkedRows="idsSelectedActivities"
            @update:checked-rows="(rows) => (idsSelectedActivities = rows)"
        >
            <!-- HEAD -->
            <CompassTableHead isFixed @sort="handleSort">
                <CompassTableHeadCell
                    v-for="column in getColumns"
                    :key="column.code"
                    :sortable="sortable[column.sortKey]"
                    :fixed="column.fixed"
                    :class="column.customClass"
                >
                    {{ column.label }}
                </CompassTableHeadCell>
                <CompassTableHeadCell fixed="right" class="activities-listing__cell-actions" />
            </CompassTableHead>

            <!-- BODY -->
            <CompassTableBody
                ref="activitiesTableBody"
                :class="{ 'reschedule-menu--is-open': !enableRowNativeEvents }"
            >
                <ActivitiesListingTableRow
                    v-for="activity in activities"
                    :key="activity.id"
                    :ref="`activity-row-${activity.id}`"
                    :columns="getColumns"
                    :activity="activity"
                    :sizeTable="sizeTable"
                    :isActivityPage="isActivityPage"
                    :isHovered="hoveredRow === activity.id"
                    @click="(event) => onClickItem(activity, event)"
                    @update-status="updateStatus"
                    @update-priority="updatePriority"
                    @enable-row-native-events="toggleEnableRowNativeEvents"
                />
            </CompassTableBody>
        </CompassTable>
        <!-- MASS ACTIONS -->
        <ActivitiesListingMassActions
            v-if="!filters.completed && isActivityPage"
            :selectedActivities="selectedActivities"
            :isToolbarVisible="!!idsSelectedActivities.length"
            :workingDays="metas.working_days"
            @close-toolbar="clearSelectedActivities"
        />
    </div>
</template>

<script>
import { updateActivity } from 'api/activities/activitiesService.js';
import ActivitiesListingTableRow from 'vcomponents/activities/listing/ActivitiesListingTableRow.vue';
import ActivitiesListingMassActions from 'vcomponents/activities/listing/massActions/ActivitiesListingMassActions.vue';
import AvatarMixins from 'mixins/AvatarMixins';
import { createNamespacedHelpers } from 'vuex';
import { TABS } from 'Dictionaries/activities/ActivitiesEnums.js';
import {
    markAsDoneWithNewForm,
    markAsTodoWithNewForm,
} from 'Helpers/activities/activitiesPhoneCallHelpers.js';
import { isEqual, cloneDeep } from 'lodash';
import Feature from 'Services/Feature';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';

const { mapActions, mapGetters } = createNamespacedHelpers('activitiesStores');
const { mapActions: listingSettingsActions } = createNamespacedHelpers('listingSettingsStores');
const { mapGetters: userMapGetters } = createNamespacedHelpers('user');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        ActivitiesListingTableRow,
        ActivitiesListingMassActions,
        TableLoading,
    },
    mixins: [AvatarMixins],
    props: {
        columns: {
            type: Array,
            required: true,
        },
        isActivityPage: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            enableRowNativeEvents: true,
            sortable: {
                date: { orderBy: 'due_date', direction: 'asc' },
                event: { orderBy: 'event', direction: '' },
                label: { orderBy: 'label', direction: '' },
                priority: { orderBy: 'priority', direction: '' },
            },
            localizedTexts: {
                labelResetFiltersBtn: this.$l10n.__('Réinitialiser'),
            },
            hoveredRow: '',
            idsSelectedActivities: [],
        };
    },
    computed: {
        ...mapGetters({
            metas: 'getMetas',
            activities: 'getActivities',
            isTableLoading: 'getLoadingTable',
            isLoadingMore: 'getLoadingMore',
            searchParams: 'getSearchParams',
            defaultFilters: 'getDefaultFilters',
            activityDataPanel: 'getActivityDataPanel',
            filters: 'getFilters',
            localStorageNameKey: 'getLocalStorageNameKey',
            isDashboardCard: 'getIsDashboardCard',
            activeTab: 'getActiveTab',
        }),
        ...userMapGetters(['getCurrentUserId', 'getIsAdmin']),
        sizeTable() {
            return this.isActivityPage ? 'large' : 'medium';
        },
        tableClasses() {
            return [
                this.isLoadingMore ? 'activities-listing-table--loading-more' : '',
                this.isActivityPage
                    ? 'activities-listing-table--activities-page'
                    : 'activities-listing-table--dashboard-card',
            ];
        },
        emptyStateDatas() {
            if (isEqual(this.defaultFilters, this.filters)) {
                return {
                    illustrationPath: '/ressources/logged/img/illustrations/empty-activities.svg',
                    title: '',
                    caption: this.$l10n.__('Aucune activité programmée pour le moment'),
                };
            } else {
                return {
                    illustrationPath: '/ressources/logged/img/illustrations/no-results.svg',
                    title: this.$l10n.__("Aucun résultat n'a été trouvé"),
                    caption: this.$l10n.__('Essayez de réinitialiser vos filtres'),
                };
            }
        },
        getColumns() {
            const columns = cloneDeep(this.columns.filter((column) => column.isVisible));
            return this.isActivityPage
                ? columns
                : columns.filter((column) => !['title', 'staff'].includes(column.code));
        },
        shouldDisplayEmptyState() {
            return this.activities.length === 0 && !this.isTableLoading && !this.isLoadingMore;
        },
        shouldDisplayTable() {
            return this.activities.length > 0 && !this.isTableLoading;
        },
        shouldDisplayMassActionsCheckbox() {
            return !this.filters.completed && this.isActivityPage;
        },
        selectedActivities() {
            this.closeDetailPanel();
            const selectedActivities = [];
            if (!this.idsSelectedActivities.length) {
                return selectedActivities;
            }
            for (const idSelectedActivity of this.idsSelectedActivities) {
                const type = idSelectedActivity.split('_')?.at(0) ?? '';
                const id = +(idSelectedActivity.split('_')?.at(1) ?? 0);
                const activity = this.activities.find(
                    (activity) => activity.activityId === id && activity.type === type,
                );
                if (activity) {
                    selectedActivities.push(activity);
                }
            }
            return selectedActivities;
        },
    },
    watch: {
        columns: {
            handler(newCols, oldCols) {
                const sortKeySearchParams =
                    this.searchParams.order === 'due_date' ? 'date' : this.searchParams.order;
                const oldColumnSorted = oldCols.find(
                    ({ sortKey }) => sortKey === sortKeySearchParams,
                );
                const newColumnSorted = newCols.find(
                    ({ sortKey }) => sortKey === sortKeySearchParams,
                );
                if (oldColumnSorted?.isVisible !== newColumnSorted?.isVisible) {
                    this.resetSortDirection().then(() => this.refreshActivities());
                }
            },
            deep: true,
        },
    },
    created() {
        const sortStorage = JSON.parse(
            localStorage.getItem(`${this.localStorageNameKey}:sort_${this.getCurrentUserId}`),
        );
        if (sortStorage) {
            for (const sortableElement of Object.values(this.sortable)) {
                if (sortableElement.orderBy === sortStorage.order) {
                    sortableElement.direction = sortStorage.direction;
                } else {
                    sortableElement.direction = '';
                }
            }
        }
        EventBus.on('activities-listing-table:refresh', this.refreshActivities);
        EventBus.on('activities-listing-table:updateSortDirection', this.handleSortOnTabUpdate);
        EventBus.on('activities-listing-table:updatePriority', this.updatePriority);
        EventBus.on('activities-listing-table:updateDueDate', this.updateDueDate);
        EventBus.on('activity-detail-panel:updateStatus', this.updateStatus);
    },
    beforeUnmount() {
        document.querySelector('.activity-detail-panel')?.remove();
        EventBus.off('activities-listing-table:refresh', this.refreshActivities);
        EventBus.off('activities-listing-table:updateSortDirection', this.handleSortOnTabUpdate);
        EventBus.off('activities-listing-table:updatePriority', this.updatePriority);
        EventBus.off('activities-listing-table:updateDueDate', this.updateDueDate);
        EventBus.off('activity-detail-panel:updateStatus', this.updateStatus);
    },
    methods: {
        isEqual,
        ...mapActions([
            'clearActivities',
            'fetchActivitiesCrm',
            'updateLoadingMore',
            'updateLoadingTable',
            'updateSearchParams',
            'updateFilters',
            'updateActivityDueDate',
            'updateActivitySelected',
        ]),
        ...listingSettingsActions(['resetPagination']),
        ...popupPanelMapActions(['open', 'close', 'feedProps']),
        async handleSort(sortParams) {
            if (sortParams?.direction !== '') {
                for (const sortKey in this.sortable) {
                    if (this.sortable[sortKey].orderBy === sortParams.orderBy) {
                        let sortDirection = this.sortable[sortKey].direction;

                        //first sort on column
                        if (this.sortable[sortKey].direction === '') {
                            sortDirection = sortKey === 'priority' ? 'desc' : 'asc';
                            this.sortable[sortKey].direction = sortDirection;
                        } else {
                            //no empty direction for date column
                            this.sortable[sortKey].direction =
                                sortKey === 'date'
                                    ? sortParams.direction
                                    : this.sortable[sortKey].direction;
                            sortDirection = sortParams.direction;
                        }
                        await this.updateSearchParams({
                            order: sortParams.orderBy,
                            direction: sortDirection,
                        });
                    } else {
                        this.sortable[sortKey].direction = '';
                    }
                }
            } else {
                await this.resetSortDirection();
            }
            this.refreshActivities();
        },

        async handleSortOnTabUpdate() {
            if (this.searchParams.order === 'due_date') {
                await this.resetSortDirection();
            }
            this.refreshActivities();
        },
        async resetSortDirection() {
            localStorage.removeItem(`${this.localStorageNameKey}:sort_${this.getCurrentUserId}`);

            const sortDirection = this.filters.completed ? 'desc' : 'asc';
            await this.updateSearchParams({
                order: 'due_date',
                direction: sortDirection,
            });
            for (const sortKey in this.sortable) {
                this.sortable[sortKey].direction = sortKey === 'date' ? sortDirection : '';
            }
        },
        refreshActivities() {
            setTimeout(async () => {
                if (this.isActivityPage) {
                    await this.resetPagination();
                }
                this.closeDetailPanel();
                await this.updateSearchParams({ offset: '' });
                await this.clearActivities();
                this.clearSelectedActivities();
                await this.fetchActivitiesCrm();
            }, 0);
        },
        displayError(text) {
            this.$pushToast({
                type: 'error',
                icon: true,
                unique: true,
                text:
                    text ?? this.$l10n.__('Une erreur est survenue, merci de réessayer plus tard'),
            });
        },
        updatePriority(data) {
            const priority = data.priority ?? data.priority;
            const id = data.id ?? data.id;
            const type = data.type ?? data.type;
            const activity = this.activities.find(
                (activity) => activity.activityId === id && activity.type === type,
            );
            const priorityValue = priority === 0 ? 1 : 0;
            const shouldTriggerPanelSync =
                this.activityDataPanel?.id === id && this.activityDataPanel?.type === type;
            if (shouldTriggerPanelSync) {
                EventBus.emit('activity-detail-panel:update-priority-pending', true);
            }

            if (activity) {
                // only if row exists in displayed listing of the current tab
                activity.prioritisation.pending = !activity.prioritisation.pending;
            }

            this.toggleEnableRowNativeEvents(false);
            updateActivity(activity.type, id, { priority: priorityValue })
                .then((res) => {
                    if (shouldTriggerPanelSync) {
                        EventBus.emit(
                            'activity-detail-panel:update-priority-value',
                            res.data.priority,
                        );
                    }
                    if (activity) {
                        activity.prioritisation.priority = priorityValue;
                    }
                })
                .catch((err) => {
                    this.displayError(err);
                })
                .finally(() => {
                    this.toggleEnableRowNativeEvents(true);
                    if (shouldTriggerPanelSync) {
                        EventBus.emit('activity-detail-panel:update-priority-pending', false);
                    }
                    if (activity) {
                        activity.prioritisation.pending = !activity.prioritisation.pending;
                    }
                });
        },
        updateStatusLocally(id, isFromPanel, activityType) {
            const activity = this.activities.find(
                (activity) => activity.activityId === id && activity.type === activityType,
            );
            const rowElement = this.$refs[`activity-row-${activity.id}`][0];
            if (isFromPanel) {
                document.getElementById('sellsy_app').scrollTo({
                    top: rowElement.$el.offsetTop + rowElement.$el.offsetHeight,
                    behavior: 'smooth',
                });
            }
            activity.status.done = !activity.status.done;
        },
        updateStatus(data) {
            const isFromPanel = data.isFromPanel ?? false;
            const activity = data.activity ?? data.activity;
            const createNewTask = data.createNewTask ?? data.createNewTask;
            const id = activity.activityId ?? activity.id;
            const row = this.activities.find(
                (row) => row.activityId === id && row.type === activity.type,
            );
            if (activity.type === 'phonecall') {
                const isDone = activity.status?.done ?? activity.result?.id !== 'scheduled';
                if (Feature.isEnabled('activity-new-creation-panel')) {
                    if (!isDone) {
                        markAsDoneWithNewForm({
                            callback: () => {
                                this.updateStatusLocally(id, isFromPanel, activity.type);
                            },
                            id,
                            date: activity.due_date ?? activity.date,
                            description: activity.description,
                        });
                    } else {
                        row.status.pending = true;
                        markAsTodoWithNewForm({ id })
                            .then(() => {
                                this.updateStatusLocally(id, isFromPanel, activity.type);
                            })
                            .finally(() => {
                                row.status.pending = false;
                                this.closeDetailPanel();
                            });
                    }
                    return;
                }
                PhoneCall.addCall(
                    activity.related[0]?.id,
                    activity.related[0]?.type,
                    id,
                    () => this.updateStatusLocally(id, isFromPanel, activity.type),
                    !isDone,
                );
            } else {
                const payload = {
                    status: activity.status.done || activity.status === 'done' ? 'todo' : 'done',
                };
                if (!isFromPanel) {
                    this.closeDetailPanel();
                }
                row.status.pending = true;

                updateActivity(activity.type, id, payload)
                    .then(() => {
                        this.updateStatusLocally(id, isFromPanel, activity.type);
                        const text =
                            payload.status === 'done'
                                ? this.$t.__('activities.markAsDone.success')
                                : this.$t.__('activities.markAsTodo.success');
                        this.$pushToast({
                            type: 'success',
                            icon: true,
                            close: true,
                            text,
                        });
                    })
                    .catch((err) => {
                        const text =
                            err.response.status === 403
                                ? this.$t.__('common.labels.no_permissions_to_perform_this_action')
                                : this.$t.__('common.labels.error_retry');
                        this.displayError(text);
                    })
                    .finally(() => {
                        row.status.pending = false;
                        if (!createNewTask) {
                            this.closeDetailPanel();
                        } else {
                            this.open({
                                key: 'activityFormPanel',
                                props: {
                                    id: null,
                                    related: row.related[0],
                                    callback: this.refreshActivities(),
                                    activityType: 'task',
                                },
                            });
                        }
                    });
            }
        },
        updateDueDate(data) {
            const { activity, rescheduledDate } = data;
            this.closeDetailPanel();
            this.updateActivityDueDate({ activity, rescheduledDate })
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        icon: true,
                        close: true,
                        text: this.$t.__('activities.common.rescheduleSuccess'),
                    });
                    setTimeout(() => this.refreshActivities(), 200);
                })
                .catch((err) => {
                    const text =
                        err.response.status === 403
                            ? this.$t.__('common.labels.no_permissions_to_perform_this_action')
                            : this.$t.__('common.labels.error_retry');
                    this.updateLoadingTable(false);
                    this.displayError(text);
                });
        },
        async onClickItem(activity, event) {
            if (
                event.target.closest('.compass-icon') ||
                event.target.closest('.compass-icon-button') ||
                event.target.closest('.compass-checkbox') ||
                !!this.idsSelectedActivities.length
            ) {
                //Avoid opening panel when clicking on the compass-icon / button /checkbox
                this.closeDetailPanel();
                return;
            }
            this.$nextTick(() => {
                if (this.enableRowNativeEvents) {
                    if (this.$store.state.popupPanel?.activityFormPanel?.display) {
                        this.$store.dispatch('popupPanel/close', { key: 'activityFormPanel' });
                    }
                    if (this.$store.state.popupPanel?.phoneCallFormPanel?.display) {
                        this.$store.dispatch('popupPanel/close', { key: 'phoneCallFormPanel' });
                    }
                    if (this.shouldOpenOldDetailPanel(activity)) {
                        return;
                    }

                    const relatedObjects = [];
                    for (const related of activity.related) {
                        if (relatedObjects.findIndex((item) => related.type === item.type) === -1) {
                            relatedObjects.push(related);
                        }
                    }

                    const activityDetailDisplayed = {
                        id: activity.activityId,
                        type: activity.type,
                        isLate: activity.isLate,
                        priority: { pending: false, value: activity.prioritisation.priority },
                        related: relatedObjects,
                    };
                    const callback = () => {
                        !this.isActivityPage
                            ? this.refreshActivities()
                            : EventBus.emit('activities-listing-table:refresh');
                    };

                    const props = {
                        activityDetailDisplayed,
                        callback: callback,
                        callbackOnDelete: callback,
                        callbackOnCreate: callback,
                    };

                    this.updateActivitySelected(activity);
                    this.open({
                        key: 'activityDetailPanel',
                        props,
                    });
                }
            });
        },
        shouldOpenOldDetailPanel(activity) {
            if (['delivery', 'proforma'].includes(activity.related.at(0)?.type ?? '')) {
                Activities.openOldTaskDetail(
                    activity.activityId,
                    activity.acl.can_be_updated,
                    activity.date,
                    activity.related.at(0)?.type,
                );
                return true;
            }
            return false;
        },
        toggleEnableRowNativeEvents(value) {
            this.enableRowNativeEvents = value;
        },
        async resetFilters() {
            const filters = { ...this.defaultFilters, completed: this.filters.completed };
            if (this.isDashboardCard && !!this.filters.due_date) {
                filters.due_date = this.filters.due_date;
            } else if (!this.isDashboardCard && this.activeTab === TABS.LATE) {
                filters.due_date = { end: moment().toISOString() };
            }

            await this.updateFilters(filters);
            this.refreshActivities();
            EventBus.emit('activities-listing-table:resetFilters');
        },
        closeDetailPanel() {
            this.updateActivitySelected(null);
            this.close({ key: 'activityDetailPanel' });
        },
        clearSelectedActivities() {
            this.idsSelectedActivities = [];
        },
    },
};
</script>

<style lang="scss" scoped>
.activities-listing-table {
    &__container {
        overflow: auto;
    }
    .reschedule-menu--is-open tr:not(.activities-listing-table__row--is-hovered) td {
        pointer-events: none;
    }
}

.activities-listing__empty-state {
    background: var(--colors-common-background-default);
    padding: var(--spacing-5) 0;
}
</style>
